import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ImageOff, Sparkles } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { BULK_MAX_ROWS } from '@designdeck/shared';
import { Button, Card, CardTitle, Switch } from '@/components/ui';
import { translateErrorCode } from '@/lib/api-errors';
import {
  isBackEnabled,
  isCountingCell,
  isRowTitleMissing,
} from '@/lib/bulk/pairing';
import { proxyStorageUrl } from '@/lib/product-transformer';
import { cn } from '@/lib/utils';
import {
  selectActiveProducts,
  useBulkCreationStore,
  type BulkCell,
  type BulkStep,
} from '@/stores/bulkCreationStore';
import { BulkSummary } from './BulkSummary';
import type { BulkSubmit, BulkSubmitError } from './useBulkSubmit';

export interface CreateStepProps {
  submit: BulkSubmit;
  className?: string;
}

/** Codes whose fix lives in an earlier step, with the step to go to. */
const ERROR_STEP: Partial<Record<string, BulkStep>> = {
  bulk_reference_unavailable: 'reference',
  bulk_no_active_products: 'reference',
  bulk_invalid_products: 'reference',
  asset_not_found: 'motifs',
  bulk_row_empty: 'motifs',
  too_many_items: 'motifs',
};

function OverviewThumb({ cell }: { cell: BulkCell | undefined }) {
  const { t } = useTranslation();
  const url = cell ? proxyStorageUrl(cell.url) : null;
  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-control bg-paper ring-1 ring-line-2">
      {url ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          className="h-full w-full object-contain"
        />
      ) : (
        <>
          <ImageOff className="h-4 w-4 text-mute" aria-hidden="true" />
          <span className="sr-only">
            {t('seller.bulk.create.overview.none')}
          </span>
        </>
      )}
    </span>
  );
}

/**
 * Step 03 of Bulk Creation (plan U10, R27–R32, R45, R54, R56): summary with
 * warnings on the left, the AI note and a read-only front/back overview on
 * the right, the draft option, and why the CTA is locked or why the start
 * failed. The CTA itself sits in the flow header; the request runs in the
 * page (`useBulkSubmit`).
 */
export function CreateStep({ submit, className }: CreateStepProps) {
  const { t } = useTranslation();
  const draftSwitchId = useId();
  const { reference, activeProductIds, rows, asDraft, setAsDraft, goToStep } =
    useBulkCreationStore(
      useShallow((state) => ({
        reference: state.reference,
        activeProductIds: state.activeProductIds,
        rows: state.rows,
        asDraft: state.asDraft,
        setAsDraft: state.setAsDraft,
        goToStep: state.goToStep,
      }))
    );

  const activeProducts = useMemo(
    () => selectActiveProducts({ reference, activeProductIds }),
    [reference, activeProductIds]
  );
  const backEnabled = isBackEnabled(activeProducts);
  const designRows = rows.filter(
    (row) =>
      isCountingCell(row.front, 'front', backEnabled) ||
      isCountingCell(row.back, 'back', backEnabled)
  );

  const overviewGrid = backEnabled
    ? 'grid-cols-[1.5rem_2.75rem_2.75rem_minmax(0,1fr)]'
    : 'grid-cols-[1.5rem_2.75rem_minmax(0,1fr)]';

  const blockText = (() => {
    switch (submit.block) {
      case 'referenceIncomplete':
        return t('seller.bulk.create.referenceIncomplete');
      case 'motifsMissing':
        return t('seller.bulk.create.motifsMissing');
      case 'uploadsPending':
        return t('seller.bulk.create.uploadsPending');
      case 'titleMissing':
        return t('seller.bulk.create.titleMissing', {
          count: rows.filter((row) => isRowTitleMissing(row, backEnabled))
            .length,
        });
      case 'tooManyRows':
        return t('seller.bulk.create.tooManyRows', { limit: BULK_MAX_ROWS });
      case 'accountNotLinked':
        return t('seller.bulk.create.accountNotLinked');
      case null:
        return null;
    }
  })();

  const errorText = (error: BulkSubmitError): string => {
    if (error.code === 'account_not_linked') {
      return t('seller.bulk.create.accountNotLinked');
    }
    if (error.code === 'too_many_items') {
      return t('seller.bulk.create.tooManyRows', { limit: BULK_MAX_ROWS });
    }
    return (
      translateErrorCode(error.code, error.params) ??
      t('seller.bulk.create.error.generic')
    );
  };
  const errorStep = submit.error?.code
    ? ERROR_STEP[submit.error.code]
    : undefined;
  // The account hint already shows as the error; do not repeat it.
  const showBlockHint =
    blockText !== null &&
    !(
      submit.block === 'accountNotLinked' &&
      submit.error?.code === 'account_not_linked'
    );

  return (
    <section
      aria-labelledby="bulk-create-title"
      className={cn('flex flex-col gap-6', className)}
    >
      <div className="flex flex-col gap-1.5">
        <h2 id="bulk-create-title" className="dc-h2 text-xl text-ink">
          {t('seller.bulk.create.title')}
        </h2>
        <p className="max-w-prose text-sm text-ink-2">
          {t('seller.bulk.create.intro')}
        </p>
      </div>

      {submit.error && (
        <div
          role="alert"
          data-testid="bulk-create-error"
          className="flex flex-wrap items-start gap-3 rounded-card bg-paper-2 px-4 py-3 ring-1 ring-inset ring-danger"
        >
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-danger"
            aria-hidden="true"
          />
          <p className="min-w-0 flex-1 text-sm text-ink">
            {errorText(submit.error)}
          </p>
          {errorStep && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToStep(errorStep)}
            >
              {errorStep === 'reference'
                ? t('seller.bulk.create.error.toReference')
                : t('seller.bulk.create.error.toMotifs')}
            </Button>
          )}
        </div>
      )}

      {showBlockHint && (
        <p
          aria-live="polite"
          data-testid="bulk-create-block"
          className="rounded-card bg-paper-2 px-4 py-3 text-sm text-ink-2 ring-1 ring-inset ring-line"
        >
          {blockText}
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
        <div className="flex min-w-0 flex-col gap-4">
          <BulkSummary
            reference={reference}
            activeProducts={activeProducts}
            rows={rows}
            counts={submit.counts}
          />

          <Card padding="sm" className="flex items-start gap-3">
            <Switch
              id={draftSwitchId}
              checked={asDraft}
              onCheckedChange={setAsDraft}
              disabled={submit.isSubmitting}
              aria-describedby={`${draftSwitchId}-hint`}
              className="mt-0.5"
            />
            <div className="flex flex-col gap-0.5">
              <label
                htmlFor={draftSwitchId}
                className="cursor-pointer text-sm font-semibold text-ink"
              >
                {t('seller.bulk.create.asDraft.label')}
              </label>
              <p
                id={`${draftSwitchId}-hint`}
                className="text-[13px] text-ink-2"
              >
                {t('seller.bulk.create.asDraft.hint')}
              </p>
            </div>
          </Card>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <Card padding="sm" className="flex items-start gap-3">
            <Sparkles
              className="mt-0.5 h-4 w-4 shrink-0 text-primary"
              aria-hidden="true"
            />
            <div className="flex flex-col gap-1">
              <h3 className="dc-h3 text-sm text-ink">
                {t('seller.bulk.create.ai.title')}
              </h3>
              <p className="text-[13px] text-ink-2">
                {t('seller.bulk.create.ai.text')}
              </p>
            </div>
          </Card>

          <Card padding="sm" className="flex flex-col gap-3">
            <CardTitle className="mb-0">
              {t('seller.bulk.create.overview.title')}
            </CardTitle>
            <div
              aria-hidden="true"
              className={cn(
                'dc-type-label grid items-center gap-3 px-2.5 text-mute',
                overviewGrid
              )}
            >
              <span />
              <span>{t('seller.bulk.create.overview.front')}</span>
              {backEnabled && (
                <span>{t('seller.bulk.create.overview.back')}</span>
              )}
              <span />
            </div>
            <ol
              data-testid="bulk-create-overview"
              className="flex max-h-[26rem] flex-col gap-2 overflow-y-auto"
            >
              {designRows.map((row, index) => (
                <li
                  key={row.id}
                  className={cn(
                    'grid items-center gap-3 rounded-control bg-paper px-2.5 py-2 ring-1 ring-inset ring-line-2',
                    overviewGrid
                  )}
                >
                  <span className="text-right font-mono text-[12px] text-mute">
                    {index + 1}
                  </span>
                  <OverviewThumb
                    cell={
                      isCountingCell(row.front, 'front', backEnabled)
                        ? row.front
                        : undefined
                    }
                  />
                  {backEnabled && (
                    <OverviewThumb
                      cell={
                        isCountingCell(row.back, 'back', backEnabled)
                          ? row.back
                          : undefined
                      }
                    />
                  )}
                  <span className="min-w-0 truncate text-sm text-ink">
                    {row.title}
                  </span>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </section>
  );
}
